/* The room moves; the page, offer and shopping controls stay still. */
(() => {
  'use strict';
  if (window.klikRoomOpening) return;
  const instances = new WeakMap();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');

  function mount(root) {
    if (instances.has(root)) return;
    const media = root.querySelector('[data-khx-media]');
    const templates = [...root.querySelectorAll('[data-khx-template]')];
    const choices = root.querySelector('[data-khx-choices]');
    const buttons = [...root.querySelectorAll('[data-khx-select]')];
    const motionButton = root.querySelector('[data-khx-motion]');
    const product = root.querySelector('[data-khx-product]');
    const name = root.querySelector('[data-khx-name]');
    const status = root.querySelector('[data-khx-status]');
    if (!media || !templates.length || !motionButton) return;
    let current = media.querySelector('[data-khx-current]');
    let index = Number(media.dataset.khxIndex) || 0;
    let camera = null;
    let reveal = null;
    let transitions = [];
    let sequence = 0;
    let userPaused = false;
    let visible = true;
    let dead = false;
    const ar = root.dir === 'rtl';
    const duration = 9600;

    const ready = image => {
      if (!image) return Promise.reject(new Error('Missing room image'));
      if (image.complete) return image.naturalWidth ? Promise.resolve() : Promise.reject(new Error('Room unavailable'));
      return new Promise((resolve, reject) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', reject, { once: true });
      });
    };

    function motionLabel() {
      const ended = camera?.playState === 'finished';
      motionButton.setAttribute('aria-pressed', String(userPaused));
      motionButton.setAttribute('aria-label', ended
        ? (ar ? 'إعادة حركة الصورة' : 'Replay image motion')
        : userPaused ? (ar ? 'تشغيل حركة الصورة' : 'Play image motion')
        : (ar ? 'إيقاف حركة الصورة' : 'Pause image motion'));
      motionButton.querySelector('[data-khx-motion-icon]').innerHTML = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7"><path d="'+(ended ? 'M20 7v5h-5M20 12a8 8 0 1 0-2 5' : userPaused ? 'M8 5 19 12 8 19Z' : 'M8 5v14M16 5v14')+'"/></svg>';
    }

    function reconcile() {
      motionButton.hidden = reduced.matches || !camera;
      if (!camera) return;
      if (reduced.matches) {
        camera.finish();
        reveal?.finish();
      } else if (document.hidden || !visible || userPaused) {
        if (camera.playState === 'running') camera.pause();
        if (reveal?.playState === 'running') reveal.pause();
      } else {
        if (camera.playState === 'paused') camera.play();
        if (reveal?.playState === 'paused') reveal.play();
      }
      motionLabel();
    }

    function startCamera(scene) {
      if (camera) {
        // Keep an outgoing scene at its last frame throughout the dissolve.
        try { camera.commitStyles(); } catch (_) { /* Optional older-browser API. */ }
        camera.cancel();
      }
      const picture = scene.querySelector('picture');
      if (reduced.matches || !picture?.animate) {
        camera = null;
        motionButton.hidden = true;
        root.dataset.khxReady = 'true';
        return;
      }
      // One continuous optical move. No loop, scroll lock, perspective or layout animation.
      camera = picture.animate([
        { transform: picture.dataset.khxRoom === '0' ? 'translate3d(0,.6%,0) scale(1.085)' : 'translate3d(0,.4%,0) scale(1.05)' },
        { transform: 'translate3d(0,0,0) scale(1)' }
      ], { duration, easing: 'cubic-bezier(.18,.45,.3,1)', fill: 'both' });
      camera.id = 'khx-room-camera';
      camera.onfinish = () => {
        picture.style.willChange = 'auto';
        motionLabel();
      };
      picture.style.willChange = 'transform';
      root.dataset.khxReady = 'true';
      reconcile();
    }

    function finishTransition() {
      transitions.forEach(animation => animation.finish());
      transitions = [];
      media.querySelectorAll('.khx__scene').forEach(scene => {
        if (scene !== current) scene.remove();
      });
    }

    async function select(nextIndex) {
      if (!templates[nextIndex]) return;
      const token = ++sequence;
      finishTransition();
      if (nextIndex === index) {
        choices.removeAttribute('aria-busy');
        status.textContent = '';
        return;
      }
      choices.setAttribute('aria-busy', 'true');
      status.textContent = ar ? 'جارٍ تحميل الغرفة' : 'Loading room';
      const next = document.createElement('div');
      next.className = 'khx__scene';
      next.style.opacity = '0';
      next.setAttribute('aria-hidden', 'true');
      next.append(templates[nextIndex].content.cloneNode(true));
      media.append(next);
      try {
        const image = next.querySelector('img');
        await ready(image);
        if (image.decode) await image.decode().catch(() => {});
        if (dead || sequence !== token) { next.remove(); return; }
        const previous = current;
        previous.removeAttribute('data-khx-current');
        previous.setAttribute('aria-hidden', 'true');
        next.removeAttribute('aria-hidden');
        next.setAttribute('data-khx-current', '');
        current = next;
        index = nextIndex;
        media.dataset.khxIndex = String(index);
        const target = new URL(templates[index].dataset.href, location.href);
        const previousTarget = new URL(product.href);
        ['preview_theme_id', 'klik_design'].forEach(key => {
          if (previousTarget.searchParams.has(key)) target.searchParams.set(key, previousTarget.searchParams.get(key));
        });
        product.href = target.href;
        name.textContent = templates[index].dataset.name;
        buttons.forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.khxSelect) === index)));
        choices.removeAttribute('aria-busy');
        status.textContent = (ar ? 'الغرفة المعروضة: ' : 'Showing room: ') + name.textContent;
        startCamera(next);
        if (reduced.matches || userPaused || !next.animate) {
          next.style.opacity = '1';
          previous.remove();
        } else {
          // Hold the previous camera exactly where it was, then dissolve into the new room.
          const dissolve = next.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: 850, easing: 'cubic-bezier(.25,.1,.25,1)', fill: 'both'
          });
          transitions = [dissolve];
          dissolve.finished.then(() => {
            if (current === next) next.style.opacity = '1';
            previous.remove();
            dissolve.cancel();
          }).catch(() => {});
        }
      } catch (_) {
        next.remove();
        if (sequence !== token || dead) return;
        choices.removeAttribute('aria-busy');
        status.textContent = ar ? 'تعذّر تحميل الصورة. اختر غرفة أخرى أو حاول مجددًا.' : 'This room could not load. Choose another or try again.';
      }
    }

    const onChoice = event => {
      const button = event.target.closest('[data-khx-select]');
      if (button) select(Number(button.dataset.khxSelect));
    };
    const onMotion = () => {
      if (!camera) return;
      if (camera.playState === 'finished') {
        userPaused = false;
        startCamera(current);
      } else {
        userPaused = !userPaused;
        reconcile();
      }
    };
    const onPreference = () => {
      finishTransition();
      reconcile();
    };
    choices.hidden = false;
    choices.addEventListener('click', onChoice);
    motionButton.addEventListener('click', onMotion);
    document.addEventListener('visibilitychange', reconcile);
    reduced.addEventListener('change', onPreference);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      reconcile();
    }, { threshold: 0 }) : null;
    observer?.observe(media);
    ready(current.querySelector('img')).then(() => {
      if (!dead && sequence === 0) {
        if (!reduced.matches && media.animate) {
          reveal = media.animate([
            { clipPath: 'inset(0 2.5% round 6px)' },
            { clipPath: 'inset(0 0% round 0px)' }
          ], { duration: 1400, easing: 'cubic-bezier(.18,.7,.25,1)', fill: 'both' });
          reveal.id = 'khx-room-reveal';
        }
        startCamera(current);
      }
    }).catch(() => { motionButton.hidden = true; });
    instances.set(root, () => {
      dead = true;
      sequence++;
      camera?.cancel();
      reveal?.cancel();
      transitions.forEach(animation => animation.cancel());
      observer?.disconnect();
      choices.removeEventListener('click', onChoice);
      motionButton.removeEventListener('click', onMotion);
      document.removeEventListener('visibilitychange', reconcile);
      reduced.removeEventListener('change', onPreference);
      instances.delete(root);
    });
  }
  function init(scope = document) {
    if (scope.matches?.('[data-khx]')) mount(scope);
    scope.querySelectorAll('[data-khx]').forEach(mount);
  }
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-khx]').forEach(root => instances.get(root)?.());
  });
  window.klikRoomOpening = true;
})();
